import React, { useEffect, useRef, useState } from "react";
import { WithStyles } from "@material-ui/styles";
import {
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography
} from "@material-ui/core";
import { ArrowLeft } from "@material-ui/icons";
import { RouteComponentProps, useLocation } from "react-router-dom";
import styles from "./styles";
import { SetFilterPayload } from "../../redux/types/filter";
import {
  MonthlyReport,
  MonthlyReportRequestPayload
} from "../../redux/types/reports";
import ReportMonthlyContent from "./ReportMonthlyContent";
import CommunitySearch from "../../containers/CommunitySearch";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { SingleSelect } from "react-select-material-ui";

const REPORT_TIMEZONE_OPTIONS = [
  {
    value: 51,
    label: "Mountain Time — America/Denver"
  },
  {
    value: 52,
    label: "Eastern Time — America/New_York"
  },
  {
    value: 53,
    label: "Central Time — America/Chicago"
  },
  {
    value: 54,
    label: "Pacific Time — America/Dawson"
  },
  {
    value: 171,
    label: "Hawaii Time — Pacific/Honolulu"
  }
];

type ReportingPeriodMode = "month" | "range";

type Props = {
  loading: boolean;
  error: string;
  monthlyReport: MonthlyReport | null;
  loadMonthlyReport: (payload: MonthlyReportRequestPayload) => void;
  setFilterState: (payload: SetFilterPayload) => void;
} & WithStyles<typeof styles> &
  RouteComponentProps;

const wait = (ms: number) =>
  new Promise(resolve => {
    window.setTimeout(resolve, ms);
  });

const isValidDateInput = (value: string | null | undefined): boolean => {
  if (!value) {
    return false;
  }

  return /^\d{4}-\d{2}-\d{2}$/.test(value);
};

const padTwo = (value: number): string =>
  value < 10 ? `0${value}` : `${value}`;

const formatDateInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = padTwo(date.getMonth() + 1);
  const day = padTwo(date.getDate());

  return `${year}-${month}-${day}`;
};

const parseDateInputAsLocalDate = (value: string): Date | null => {
  if (!isValidDateInput(value)) {
    return null;
  }

  const [year, month, day] = value.split("-").map(Number);

  return new Date(year, month - 1, day);
};

const getToday = (): Date => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return today;
};

const isFutureDate = (date: Date): boolean => {
  const normalizedDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  return normalizedDate > getToday();
};

const getMonthStartDate = (date: Date): string =>
  formatDateInput(new Date(date.getFullYear(), date.getMonth(), 1));

const getSafeMonthEndDate = (date: Date): string => {
  const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  const today = getToday();

  return formatDateInput(monthEnd > today ? today : monthEnd);
};

const getMonthSelectionFromRange = (
  startDate: string,
  endDate: string
): Date | null => {
  const parsedStartDate = parseDateInputAsLocalDate(startDate);
  const parsedEndDate = parseDateInputAsLocalDate(endDate);

  if (!parsedStartDate || !parsedEndDate) {
    return null;
  }

  const sameMonth =
    parsedStartDate.getFullYear() === parsedEndDate.getFullYear() &&
    parsedStartDate.getMonth() === parsedEndDate.getMonth();

  if (!sameMonth) {
    return null;
  }

  const expectedStartDate = getMonthStartDate(parsedStartDate);
  const expectedEndDate = getSafeMonthEndDate(parsedStartDate);

  if (startDate !== expectedStartDate || endDate !== expectedEndDate) {
    return null;
  }

  return new Date(parsedStartDate.getFullYear(), parsedStartDate.getMonth(), 1);
};

const isValidReportingRange = (startDate: string, endDate: string): boolean => {
  const parsedStartDate = parseDateInputAsLocalDate(startDate);
  const parsedEndDate = parseDateInputAsLocalDate(endDate);

  if (!parsedStartDate || !parsedEndDate) {
    return false;
  }

  return parsedStartDate <= parsedEndDate && !isFutureDate(parsedEndDate);
};

const parseCommunityIds = (value: string | null): number[] => {
  if (!value) {
    return [];
  }

  return value
    .split(",")
    .map(item => Number(item.trim()))
    .filter(item => Number.isInteger(item) && item > 0);
};

const ReportMonthly: React.FC<Props> = props => {
  const {
    classes,
    history,
    loading,
    error,
    monthlyReport,
    loadMonthlyReport,
    setFilterState
  } = props;

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const initialBuilderId = searchParams.get("builderId") || "";
  const initialEnterpriseId = searchParams.get("enterpriseId") || "";
  const initialStartDate = searchParams.get("startDate") || "";
  const initialEndDate = searchParams.get("endDate") || "";
  const initialSelectedMonth = getMonthSelectionFromRange(
    initialStartDate,
    initialEndDate
  );

  const [builderId, setBuilderId] = useState<string>(initialBuilderId);
  const [enterpriseId, setEnterpriseId] = useState<string>(initialEnterpriseId);
  const [timezoneId, setTimezoneId] = useState<number | null>(null);

  const isEnterpriseReport = builderId === "0";

  const [reportingPeriodMode, setReportingPeriodMode] =
    useState<ReportingPeriodMode>(
      initialStartDate && initialEndDate
        ? "range"
        : "month"
    );

  const [selectedMonth, setSelectedMonth] = useState<Date | null>(
    initialSelectedMonth
  );

  const [rangeStartDate, setRangeStartDate] =
    useState<string>(initialStartDate);
  const [rangeEndDate, setRangeEndDate] = useState<string>(initialEndDate);

  const startDate =
    reportingPeriodMode === "month"
      ? selectedMonth
        ? getMonthStartDate(selectedMonth)
        : ""
      : rangeStartDate;

  const endDate =
    reportingPeriodMode === "month"
      ? selectedMonth
        ? getSafeMonthEndDate(selectedMonth)
        : ""
      : rangeEndDate;

  const today = getToday();

  const [communityIds, setCommunityIds] = useState<number[]>(
    parseCommunityIds(searchParams.get("communityIds"))
  );

  const hiddenExportRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);

  const handleExportPdf = async () => {
    if (
      !hiddenExportRef.current ||
      !monthlyReport ||
      loading ||
      isExporting ||
      !startDate ||
      !endDate
    ) {
      return;
    }

    try {
      setIsExporting(true);

      await wait(600);

      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const html2pdfModule = require("html2pdf.js");
      const html2pdf =
        html2pdfModule && html2pdfModule.default
          ? html2pdfModule.default
          : html2pdfModule;

      await html2pdf()
        .set({
          margin: [6, 6, 6, 6],
          filename: `monthly-report-${startDate}-to-${endDate}.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: {
            scale: 1.35,
            useCORS: true,
            backgroundColor: "#ffffff",
            scrollX: 0,
            scrollY: 0,
            logging: false
          },
          jsPDF: {
            unit: "mm",
            format: "a4",
            orientation: "portrait"
          },
          pagebreak: {
            mode: ["css", "legacy"]
          }
        })
        .from(hiddenExportRef.current)
        .save();
    } catch (exportError) {
      // eslint-disable-next-line no-console
      console.error("Monthly report PDF export failed", exportError);
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    const newBuilderId = searchParams.get("builderId") || "";
    const newEnterpriseId = searchParams.get("enterpriseId") || "";

    if (newBuilderId && newBuilderId !== builderId) {
      setBuilderId(newBuilderId);
      setEnterpriseId(newEnterpriseId);
      setTimezoneId(null);
      setCommunityIds([]);
      setSelectedMonth(null);
      setRangeStartDate("");
      setRangeEndDate("");
      setReportingPeriodMode("month");

      setFilterState({
        type: "reports",
        name: "builderId",
        value: newBuilderId
      });
    }
  }, [location.search, builderId, searchParams, setFilterState]);

  useEffect(() => {
    if (!builderId) {
      const newBuilderId = searchParams.get("builderId") || "";

      if (newBuilderId) {
        setBuilderId(newBuilderId);
        setFilterState({
          type: "reports",
          name: "builderId",
          value: newBuilderId
        });
      }
    }
  }, [builderId, searchParams, setFilterState]);

  useEffect(() => {
    if (
      !builderId ||
      !isValidDateInput(startDate) ||
      !isValidDateInput(endDate) ||
      !isValidReportingRange(startDate, endDate)
    ) {
      return;
    }

    if (isEnterpriseReport && timezoneId === null) {
      return;
    }

    const payload: MonthlyReportRequestPayload = {
      builderIds: builderId,
      enterpriseId: enterpriseId
        ? Number(enterpriseId)
        : null,
      startDate,
      endDate,
      communityIds: communityIds.length
        ? communityIds.join(",")
        : null
    };

    if (isEnterpriseReport && timezoneId !== null) {
      payload.timezoneId = timezoneId;
    }

    loadMonthlyReport(payload);
  }, [
    builderId,
    startDate,
    endDate,
    enterpriseId,
    communityIds,
    isEnterpriseReport,
    timezoneId,
    loadMonthlyReport
  ]);

  const handleReportingPeriodModeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nextMode = event.target.value as ReportingPeriodMode;

    if (nextMode === "month") {
      setSelectedMonth(getMonthSelectionFromRange(rangeStartDate, rangeEndDate));
    }

    if (nextMode === "range" && selectedMonth) {
      setRangeStartDate(getMonthStartDate(selectedMonth));
      setRangeEndDate(getSafeMonthEndDate(selectedMonth));
    }

    setReportingPeriodMode(nextMode);
  };

  const handleReportingMonthChange = (date: Date | null) => {
    if (!date || Number.isNaN(date.getTime()) || isFutureDate(date)) {
      setSelectedMonth(null);
      return;
    }

    setSelectedMonth(new Date(date.getFullYear(), date.getMonth(), 1));
  };

  const handleStartDateChange = (date: Date | null) => {
    if (!date || Number.isNaN(date.getTime()) || isFutureDate(date)) {
      setRangeStartDate("");
      return;
    }

    setRangeStartDate(formatDateInput(date));
  };

  const handleEndDateChange = (date: Date | null) => {
    if (!date || Number.isNaN(date.getTime()) || isFutureDate(date)) {
      setRangeEndDate("");
      return;
    }

    setRangeEndDate(formatDateInput(date));
  };

  const renderPageState = () => {
    if (
      !builderId ||
      !startDate ||
      !endDate ||
      (isEnterpriseReport && timezoneId === null)
    ) {
      return (
        <div className={classes.emptyBox}>
          <Typography variant="h6">Select a reporting period</Typography>
          <Typography variant="body2">
            {isEnterpriseReport && timezoneId === null
              ? "Choose a timezone and reporting period to load the Monthly Report."
              : "Choose a reporting period above to load the Monthly Report."}
          </Typography>
        </div>
      );
    }

    if (
      !isValidDateInput(startDate) ||
      !isValidDateInput(endDate) ||
      !isValidReportingRange(startDate, endDate)
    ) {
      return (
        <div className={classes.emptyBox}>
          <Typography variant="h6">Invalid reporting period</Typography>
          <Typography variant="body2">
            Reporting period must resolve to a valid date range that does not
            include future dates.
          </Typography>
        </div>
      );
    }

    if (loading) {
      return (
        <div className={classes.spinnerWrapper}>
          <CircularProgress />
        </div>
      );
    }

    if (error) {
      return (
        <div className={classes.errorBox}>
          <Typography variant="h6">Unable to load Monthly Report</Typography>
          <Typography variant="body2">{error}</Typography>
        </div>
      );
    }

    if (!monthlyReport) {
      return (
        <div className={classes.emptyBox}>
          <Typography variant="h6">No Monthly Report data available</Typography>
          <Typography variant="body2">
            Choose a valid reporting period to load the report.
          </Typography>
        </div>
      );
    }

    return (
      <>
        <div className={classes.exportRoot}>
          <ReportMonthlyContent
            classes={classes}
            monthlyReport={monthlyReport}
            startDate={startDate}
            endDate={endDate}
            isPrint={false}
          />
        </div>

        <div className={classes.hiddenExportHost}>
          <div
            ref={hiddenExportRef}
            className={`${classes.exportRoot} ${classes.exportMode}`}
          >
            <ReportMonthlyContent
              classes={classes}
              monthlyReport={monthlyReport}
              startDate={startDate}
              endDate={endDate}
              isPrint
            />
          </div>
        </div>
      </>
    );
  };

  return (
    <div className={classes.page}>
      <Grid
        container
        justify="space-between"
        alignItems="center"
        className={classes.topBar}
      >
        <Grid item>
          <Button variant="contained" onClick={() => history.push("/reports")}>
            <ArrowLeft />
            GoBack
          </Button>
        </Grid>
      </Grid>

      <div className={classes.controlsCard}>
        <Grid container justify="space-between" alignItems="center">
          <Grid item>
            <div className={classes.controlsTitle}>Reporting Period</div>
          </Grid>

          <Grid item>
            <Button
              variant="contained"
              color="primary"
              disabled={!monthlyReport || loading || isExporting}
              onClick={handleExportPdf}
            >
              {isExporting ? "Exporting..." : "Export PDF"}
            </Button>
          </Grid>
        </Grid>

        <RadioGroup
          row
          value={reportingPeriodMode}
          onChange={handleReportingPeriodModeChange}
        >
          <FormControlLabel value="month" control={<Radio />} label="Month" />
          <FormControlLabel
            value="range"
            control={<Radio />}
            label="Date Range"
          />
        </RadioGroup>

        <Grid container spacing={2}>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            {reportingPeriodMode === "month" ? (
              <Grid item xs={12} sm={6} md={3}>
                <KeyboardDatePicker
                  variant="inline"
                  fullWidth
                  inputVariant="outlined"
                  label="Reporting Month"
                  value={selectedMonth}
                  placeholder="Month YYYY"
                  onChange={handleReportingMonthChange}
                  format="MMMM yyyy"
                  views={["year", "month"]}
                  openTo="month"
                  disableFuture
                  maxDate={today}
                  className={classes.controlField}
                  KeyboardButtonProps={{
                    "aria-label": "change reporting month"
                  }}
                />
              </Grid>
            ) : (
              <>
                <Grid item xs={12} sm={6} md={2}>
                  <KeyboardDatePicker
                    variant="inline"
                    fullWidth
                    inputVariant="outlined"
                    label="Start Date"
                    value={parseDateInputAsLocalDate(rangeStartDate)}
                    placeholder="MM/DD/YYYY"
                    onChange={handleStartDateChange}
                    format="MM/dd/yyyy"
                    disableFuture
                    maxDate={today}
                    className={classes.controlField}
                    KeyboardButtonProps={{
                      "aria-label": "change start date"
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <KeyboardDatePicker
                    variant="inline"
                    fullWidth
                    inputVariant="outlined"
                    label="End Date"
                    value={parseDateInputAsLocalDate(rangeEndDate)}
                    placeholder="MM/DD/YYYY"
                    onChange={handleEndDateChange}
                    format="MM/dd/yyyy"
                    disableFuture
                    maxDate={today}
                    className={classes.controlField}
                    KeyboardButtonProps={{
                      "aria-label": "change end date"
                    }}
                  />
                </Grid>
              </>
            )}
          </MuiPickersUtilsProvider>
          {
            builderId !== '0' && (
              <Grid item xs={12} sm={6} md={3}>
                <div className={classes.communitySelectWrapper}>
                  <CommunitySearch
                    builderId={Number(builderId)}
                    multiple
                    creatable={false}
                    initialValue={communityIds}
                    label="Communities"
                    handleChange={(value: number | number[]) => {
                      setCommunityIds(Array.isArray(value) ? value : []);
                    }}
                  />
                </div>
              </Grid>
            )
          }

          {isEnterpriseReport && (
            <Grid item xs={12} sm={6} md={3}>
              <SingleSelect
                className={classes.timezoneSelect}
                label="Time Zone"
                options={REPORT_TIMEZONE_OPTIONS}
                value={timezoneId}
                onChange={(value: any) => {
                  setTimezoneId(typeof value === "number" ? value : null);
                }}
                SelectProps={{
                  isCreatable: false,
                  msgNoOptionsMatchFilter: "No time zones were found"
                }}
                required
              />
            </Grid>
          )}
        </Grid>
      </div>

      <div className={classes.contentWrapper}>{renderPageState()}</div>
    </div>
  );
};

export default React.memo(ReportMonthly);
